import { Box, Button, FormControl, HStack, Stack, VStack } from "@chakra-ui/react";
import { useAuthInMutation, useRefreshQuotesMutation, useTransferInMutation } from "apis";
import { ActionBar, CheckAgreement, Layout, PageMotion, PinInput, PoweredBy, Span } from "components";
import { add } from "date-fns";
import { useAuth, useDispatch, useForm, useISOCountDown, usePartialState, useSearchParams } from "hooks";
import { PageProps } from "interfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { hydrate } from "store/slices/auth";

import configs from "config";
import toLower from "lodash/toLower";
import ls from "utils/secureStorage";
import { PartnerVerification } from "pages";

export interface VerificationProps extends PageProps {}

interface IState {
  terms: boolean;
  offers: boolean;
  pin: string;
  isLoading: boolean;
  remember: boolean;
}

export function VerificationWrapper() {
  const partner = useSearchParams("type");

  return <>{!partner ? <Verification /> : <PartnerVerification />}</>;
}

export function Verification(props: VerificationProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { auth } = useAuth();

  const persistedEmail = ls.get("email");

  const tabType: "buy" | "sell" = ls.get("tabType");

  const remember = useMemo(() => Boolean(ls.get("remember")), []);
  const terms = useMemo(() => ls.get("terms"), []);
  const offers = useMemo(() => ls.get("offers"), []);

  const { quoteRes, setQuoteRes, setTransferId, setTransferData, isActiveQuote } = useForm();

  const [state, set] = usePartialState<IState>({
    remember,
    terms: String(terms).length === 0 ? true : terms,
    offers: String(offers).length === 0 ? true : offers,
  });
  const [nextRequestInSecs, setNextRequestInSecs] = useState(5);

  useEffect(() => {
    const quoteId = quoteRes?.quote?.quoteId!;
    if (auth?.token && quoteId) {
      proceedToNextStep({ quoteId, token: auth?.token });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemember = (checked: boolean) => {
    set({ remember: checked });
    ls.set("remember", checked);
  };

  const [authIn, { isLoading }] = useAuthInMutation();

  const [refreshQuote] = useRefreshQuotesMutation();
  const [transferIn] = useTransferInMutation();

  const initialTime = useMemo(() => {
    return ls.get("tmp_cd") ?? add(new Date(), { seconds: nextRequestInSecs > 10 ? nextRequestInSecs : 60 }).toISOString();
  }, [nextRequestInSecs]);

  const { timer, expired } = useISOCountDown(initialTime, "00:00", (iso, expired) => {
    if (!expired) ls.set("tmp_cd", iso);
    else ls.remove("tmp_cd");
  });

  const persistURL = useMemo(() => ls.get(configs.PERSIST_URL) as string, []);

  console.log(isActiveQuote(), "IS ACTIVE QUOTE");
  const proceedToNextStep = useCallback(async ({ quoteId, token }: { quoteId: string; token: string }) => {
    try {
      const refreshResponse = !quoteRes?.loginRequired ? quoteRes : await refreshQuote({ quoteId, token }).unwrap();

      if (refreshResponse && tabType === "sell") {
        navigate(`/payment`, { replace: true });

        return;
      }

      if (quoteId && refreshResponse) {
        setQuoteRes(refreshResponse);

        ls.set(configs.KYC_REQUIRED, refreshResponse.kyc.kycRequired);
        if (refreshResponse.kyc.kycRequired) {
          navigate("/kyc", { replace: true });
        } else {
          const transferResult = await transferIn({
            quoteId,
            token,
            type: "in",
          }).unwrap();

          setTransferId(transferResult?.transferId);
          setTransferData(transferResult);
          navigate(`/payment`, { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    } catch (err: any) {
      if (String(err?.data?.error).toLocaleLowerCase() === "accountnamerequired") {
        navigate("/account-name");
      }
      // console.log({ err });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthClick = ls.get(configs.AUTHENTICATE_CLICK);
  const handleContinue = async (e: any) => {
    e.preventDefault();
    if (!persistedEmail || !state?.pin) return;

    try {
      set({ isLoading: true });
      const result = await authIn({ email: persistedEmail, code: state?.pin, remember: state.remember! }).unwrap();

      if (!!result) dispatch(hydrate({ ...result }));

      if (checkAuthClick && !!result) {
        ls.set(configs.AUTH_TOKEN_KEY, JSON.stringify({ ...result, token: result.token, expiry: result.expiry }));

        window.location.replace(`/${persistURL}`);
        ls.set(configs.AUTHENTICATE_CLICK, false);
      }

      setNextRequestInSecs(result.nextRequestInSecs);

      if (!isActiveQuote()) {
        window.location.replace(`/${persistURL}`);
      }

      if (!!result && !!quoteRes) {
        const quoteId = quoteRes?.quote?.quoteId!;
        ls.set(configs.AUTH_TOKEN_KEY, JSON.stringify({ ...result, token: result.token, expiry: result.expiry }));

        proceedToNextStep({ quoteId, token: result.token });
      }
      // set({ isLoading: false });
    } catch (error: any) {
      set({ isLoading: false });

      console.log("Verification Error", error);

      if (toLower(error?.data?.error) === "kycrequired") navigate("/kyc", { replace: true });
      if (toLower(error?.data?.error) === "invalidcode") navigate("/", { replace: true });
    }
  };

  const handleResendCode = async () => {
    const result = await authIn({ email: persistedEmail, remember: state.remember! }).unwrap();

    if (result) {
      setNextRequestInSecs(Number(result.nextRequestInSecs));
    }
  };

  const isDisabled = useMemo(
    () => !state?.terms || !state?.pin || (!!state?.pin && state.pin.length < 6) || !!state?.isLoading,
    [state]
  );

  useEffect(() => {
    if (!ls.get("tmp_cd")) ls.set("tmp_cd", add(new Date(), { seconds: 60 }).toISOString());
  }, [nextRequestInSecs]);

  // useEffect(() => {
  //   if (!quoteRes) navigate("/", { replace: true });
  // }, [quoteRes, navigate]);

  if (auth?.token && quoteRes?.quote?.quoteId) {
    return null;
  }

  return (
    <PageMotion key="Verification" px="4px" minH={{ base: "96vh", md: "98vh" }}>
      <Layout>
        <ActionBar onBackClick={() => navigate(-1)} title="Verification code">
          Please enter the verification code sent to {persistedEmail}.
        </ActionBar>

        {/* <VStack my="36px">
          <Box
            bg="#CFDAD9"
            boxSize="104px"
            borderRadius="50%"
            p="20px"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box as={VerificationSVG} />
          </Box>
        </VStack> */}

        <Stack
          as="form"
          my={{ base: "16px !important", md: "34px !important" }}
          px="3px"
          // onSubmit={checkAuthClick ? handleAuthenticate : handleContinue}
          onSubmit={handleContinue}
        >
          <FormControl>
            <PinInput onChange={(pin) => set({ pin })} />
            <HStack mt="20px" gridGap="4px" sx={{ "> *": { ml: "0 !important" } }}>
              <Span fontWeight="500">Didn't receive it?</Span>
              <Span fontWeight="700" onClick={handleResendCode}>
                Resend code
              </Span>
              {!expired && (
                <Span fontWeight="700" color="secondary.default">
                  {timer}
                </Span>
              )}
              {!!expired && (
                <Button isLoading={isLoading} onClick={handleResendCode} variant="max">
                  Send
                </Button>
              )}
            </HStack>
          </FormControl>

          <Stack mt={{ base: "24px !important", md: "46px !important" }}>
            <CheckAgreement mb="16px !important" isChecked={state.remember} onCheck={handleRemember}>
              Remember Me
            </CheckAgreement>

            <CheckAgreement
              isChecked={state?.terms}
              onCheck={(ck) => {
                set({ terms: ck });
                ls.set("terms", ck);
              }}
            >
              I agree with Bitmama's{" "}
              <Box as="a" href="https://bitmama.io/terms/" target="_blank" fontWeight="700">
                Terms of Use
              </Box>{" "}
              and{" "}
              <Box as="a" href="https://bitmama.io/privacy/" target="_blank" fontWeight="700">
                Privacy Policy.
              </Box>
            </CheckAgreement>

            <CheckAgreement
              mt="16px !important"
              isChecked={state?.offers}
              onCheck={(ck) => {
                set({ offers: ck });
                ls.set("offers", ck);
              }}
            >
              Send me news and exclusive offers from Bitmama and its partners. I'll get marketing cookies. I know I can opt out at
              any time.
            </CheckAgreement>
          </Stack>

          <VStack mt={{ base: "32px !important", md: "70px !important" }}>
            <Button
              minW={{ base: "auto", "2sm": "400px" }}
              w="100%"
              type="submit"
              isDisabled={isDisabled}
              isLoading={!!state?.isLoading}
              _hover={{
                _disabled: {
                  opacity: 0.3,
                },
              }}
            >
              Continue
            </Button>
          </VStack>
        </Stack>
      </Layout>

      <PoweredBy />
    </PageMotion>
  );
}
